import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { APP_LOGO, APP_TITLE } from "@shared/const";
import { trpc } from "@/lib/trpc";
import { Building2, Loader2, User, Briefcase } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

/**
 * MyPA Profile Completion Page
 * Guided profile setup with progressive job selection: Sector → Subsector → Activity
 */
export default function ProfileComplete() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [step, setStep] = useState(1);

  // Form state
  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [phone, setPhone] = useState("");
  const [sector, setSector] = useState("");
  const [subsector, setSubsector] = useState("");
  const [activityCode, setActivityCode] = useState("");
  const [country, setCountry] = useState("");

  // Load sectors
  const { data: sectors, isLoading: loadingSectors } = trpc.jobs.sectors.useQuery();

  // Load subsectors when sector changes
  const { data: subsectors, isLoading: loadingSubsectors } = trpc.jobs.subsectors.useQuery(
    { sector },
    { enabled: !!sector }
  );

  // Load activities when subsector changes
  const { data: activities, isLoading: loadingActivities } = trpc.jobs.activities.useQuery(
    { subsector },
    { enabled: !!subsector }
  );

  // Reset dependent fields when parent changes
  useEffect(() => {
    setSubsector("");
    setActivityCode("");
  }, [sector]);

  useEffect(() => {
    setActivityCode("");
  }, [subsector]);

  const completeProfile = trpc.profile.complete.useMutation({
    onSuccess: () => {
      toast.success("Profile completed successfully!");
      setLocation("/dashboard");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to complete profile");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (step === 1) {
      if (!companyName || !firstName || !lastName) {
        toast.error("Please fill in all required fields");
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!sector || !subsector || !activityCode || !country) {
        toast.error("Please fill in all required fields");
        return;
      }
      
      completeProfile.mutate({
        companyName,
        firstName,
        lastName,
        phone,
        jobCode: activityCode,
        sector,
        subsector,
        country,
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="w-full border-b px-4 flex items-center justify-between h-16 bg-gradient-mypa">
        <div className="flex items-center gap-3">
          <img
            src={APP_LOGO}
            alt="MyPA Logo"
            className="h-10 w-10 rounded-lg object-contain"
          />
          <span className="text-xl font-bold text-white">{APP_TITLE}</span>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-6 bg-gradient-mypa-light">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
            <CardDescription>
              {step === 1
                ? "Tell us about your company and yourself"
                : "Select your job and location"}
            </CardDescription>
            <div className="flex justify-center gap-2 mt-4">
              <div
                className={`h-2 w-16 rounded-full ${
                  step === 1 ? "bg-primary" : "bg-muted"
                }`}
              />
              <div
                className={`h-2 w-16 rounded-full ${
                  step === 2 ? "bg-primary" : "bg-muted"
                }`}
              />
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 1 ? (
                <>
                  {/* Step 1: Company and Personal Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <Building2 className="h-5 w-5 text-primary" />
                      <span>Company Information</span>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="companyName">
                        Company Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="companyName"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Enter your company name"
                        required
                      />
                    </div>

                    <div className="flex items-center gap-2 text-lg font-semibold mt-6">
                      <User className="h-5 w-5 text-primary" />
                      <span>Personal Information</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">
                          First Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="firstName"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="First name"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lastName">
                          Last Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="lastName"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Last name"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+221 XX XXX XX XX"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-mypa hover:opacity-90"
                    size="lg"
                  >
                    Continue
                  </Button>
                </>
              ) : (
                <>
                  {/* Step 2: Progressive Job Selection */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                      <Briefcase className="h-5 w-5 text-primary" />
                      <span>Professional Activity</span>
                    </div>

                    {/* Sector Selection */}
                    <div className="space-y-2">
                      <Label htmlFor="sector">
                        1. Sector <span className="text-destructive">*</span>
                      </Label>
                      <Select value={sector} onValueChange={setSector} disabled={loadingSectors}>
                        <SelectTrigger>
                          <SelectValue placeholder={loadingSectors ? "Loading sectors..." : "Select your sector"} />
                        </SelectTrigger>
                        <SelectContent>
                          {sectors?.map((s: string) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        Choose the main sector of your business activity
                      </p>
                    </div>

                    {/* Subsector Selection */}
                    {sector && (
                      <div className="space-y-2">
                        <Label htmlFor="subsector">
                          2. Subsector <span className="text-destructive">*</span>
                        </Label>
                        <Select 
                          value={subsector} 
                          onValueChange={setSubsector}
                          disabled={loadingSubsectors || !sector}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={loadingSubsectors ? "Loading subsectors..." : "Select your subsector"} />
                          </SelectTrigger>
                          <SelectContent>
                            {subsectors?.map((ss: string) => (
                              <SelectItem key={ss} value={ss}>
                                {ss}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground">
                          Narrow down your specific area of activity
                        </p>
                      </div>
                    )}

                    {/* Activity Selection */}
                    {subsector && (
                      <div className="space-y-2">
                        <Label htmlFor="activity">
                          3. Activity <span className="text-destructive">*</span>
                        </Label>
                        <Select 
                          value={activityCode} 
                          onValueChange={setActivityCode}
                          disabled={loadingActivities || !subsector}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={loadingActivities ? "Loading activities..." : "Select your activity"} />
                          </SelectTrigger>
                          <SelectContent>
                            {activities?.map((act) => (
                              <SelectItem key={act.id} value={act.id}>
                                {act.job_name_en}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground">
                          This determines which emission factors are relevant for your business
                        </p>
                      </div>
                    )}

                    {/* Country Selection */}
                    <div className="space-y-2 mt-6">
                      <Label htmlFor="country">
                        Country <span className="text-destructive">*</span>
                      </Label>
                      <Select value={country} onValueChange={setCountry}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SEN">Senegal</SelectItem>
                          <SelectItem value="CIV">Côte d'Ivoire</SelectItem>
                          <SelectItem value="MAR">Morocco</SelectItem>
                          <SelectItem value="TUN">Tunisia</SelectItem>
                          <SelectItem value="KEN">Kenya</SelectItem>
                          <SelectItem value="GHA">Ghana</SelectItem>
                          <SelectItem value="NGA">Nigeria</SelectItem>
                          <SelectItem value="ZAF">South Africa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="bg-muted p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        <strong>Important:</strong> Once you complete your profile, company information and credentials will be locked.
                        Only a MyPA administrator can unlock them if you need to make changes. Your preferences will remain editable.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setStep(1)}
                      disabled={completeProfile.isPending}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-gradient-mypa hover:opacity-90"
                      size="lg"
                      disabled={completeProfile.isPending}
                    >
                      {completeProfile.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Completing...
                        </>
                      ) : (
                        "Complete Profile"
                      )}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

